import type { SxProps, Theme } from "@mui/material";
import {
  Box,
  Button,
  CardContent,
  CircularProgress,
  Card as MuiCard,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { BORDER_RADIUS, COLORS } from "app/theme";
import { AnalyticsEvents, analytics } from "common/analytics";
import HorizontalScrollContainer from "common/components/HorizontalScrollContainer";
import { useCurrentFarmAnimalGroups } from "model/useAnimalGroupQueries";
import { useFields } from "model/useFieldQueries";
import { useFarmerProgrammeType } from "model/useProgrammeActionsQueries";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import {
  useBiodiversityScalars,
  useBirdScalar,
  useCarcaseGauges,
  useEmissionsBySource,
  useEmissionsPcf,
  useGrazingEventsByField,
  useMilkQualityFarmer,
  useNutrientStrengths,
  useNutrientWeaknesses,
} from "./hooks/useInsightsDashboardQueries";
import type { InsightCategory } from "./InsightDetailDialog";

const InsightCard = styled(MuiCard)({
  backgroundColor: "white",
  borderRadius: BORDER_RADIUS.medium,
  cursor: "pointer",
  transition: "transform 0.2s, box-shadow 0.2s",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  },
});

function CardHeader({
  title,
  description,
  icon,
}: {
  title: string;
  description: string;
  icon?: React.ReactNode;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
      {icon && (
        <Box
          sx={{
            fontSize: "24px",
            minWidth: "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>
      )}
      <Box>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, color: "#18181b", lineHeight: 1.3 }}
        >
          {title}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "#a1a1aa", lineHeight: 1.3 }}
        >
          {description}
        </Typography>
      </Box>
    </Box>
  );
}

function CardLoading() {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
      <CircularProgress size={20} />
    </Box>
  );
}

const CHART_COLORS = [
  COLORS.dahlia[500],
  COLORS.bellflower[500],
  COLORS.daisy[500],
  COLORS.dahlia[300],
  COLORS.bellflower[300],
  COLORS.daisy[300],
];

type InsightCardProps = {
  onClick: () => void;
  farmerGuid?: string;
  sx?: SxProps<Theme>;
};

// --- Carbon Footprint Card ---

export function CarbonFootprintCard({
  onClick,
  farmerGuid,
  sx,
}: InsightCardProps) {
  const { data: pcfData, isLoading: pcfLoading } = useEmissionsPcf(farmerGuid);
  const { data: sourceData, isLoading: sourceLoading } =
    useEmissionsBySource(farmerGuid);

  const isLoading = pcfLoading || sourceLoading;
  const pcfValue = pcfData?.data?.[0]?.["Emissions.avgValue"];
  const sources = (sourceData?.data ?? [])
    .map((r) => ({
      name: r["Emissions.parameter"],
      value: Number(r["Emissions.avgValue"]),
    }))
    .filter((s) => s.value > 0)
    .slice(0, 5);

  const total = sources.reduce((sum, s) => sum + s.value, 0);

  const hasData = pcfValue != null || sources.length > 0;

  return (
    <InsightCard
      elevation={1}
      onClick={hasData ? onClick : undefined}
      sx={[
        !hasData ? { cursor: "default" } : {},
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        <CardHeader
          title="Carbon Footprint"
          description="Emissions by source"
        />
        <Box sx={{ pt: 1.5, borderTop: "1px solid #f4f4f5" }}>
          {isLoading ? (
            <CardLoading />
          ) : !hasData ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.4 }}
            >
              No emissions data available yet. Carbon footprint insights will
              appear here once your programme data has been processed.
            </Typography>
          ) : (
            <>
              {pcfValue != null && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: "#18181b", lineHeight: 1 }}
                  >
                    {Number(pcfValue).toFixed(1)}
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ color: "#71717a", ml: 0.5 }}
                    >
                      kg CO₂e/kg
                    </Typography>
                  </Typography>
                </Box>
              )}
              {sources.length > 0 && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box sx={{ width: 90, height: 90, flexShrink: 0 }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={sources}
                          cx="50%"
                          cy="50%"
                          innerRadius={22}
                          outerRadius={40}
                          dataKey="value"
                          paddingAngle={2}
                          stroke="none"
                        >
                          {sources.map((_, i) => (
                            <Cell
                              key={i}
                              fill={CHART_COLORS[i % CHART_COLORS.length]}
                            />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                      flex: 1,
                    }}
                  >
                    {sources.slice(0, 4).map((s, i) => (
                      <Box
                        key={s.name}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            backgroundColor:
                              CHART_COLORS[i % CHART_COLORS.length],
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#71717a",
                            lineHeight: 1.2,
                            flex: 1,
                          }}
                        >
                          {s.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: "#18181b",
                            flexShrink: 0,
                          }}
                        >
                          {total > 0
                            ? `${Math.round((s.value / total) * 100)}%`
                            : ""}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
      </CardContent>
    </InsightCard>
  );
}

// --- Biodiversity Card ---

export function BiodiversityCard({
  onClick,
  farmerGuid,
  sx,
}: InsightCardProps) {
  const { data: bioData, isLoading: bioLoading } =
    useBiodiversityScalars(farmerGuid);
  const { data: birdData, isLoading: birdLoading } = useBirdScalar(farmerGuid);

  const isLoading = bioLoading || birdLoading;
  const bioRow = bioData?.data?.[0];
  const birdCount = birdData?.data?.[0]?.["BirdObservations.distinctBirdCount"];

  const habitatCount = bioRow?.["Biodiversity.distinctHabitatCodeCount"];
  const hedgerows =
    bioRow?.["Biodiversity.totalLengthM"] != null
      ? Math.round(Number(bioRow["Biodiversity.totalLengthM"]))
      : null;
  const woodland =
    bioRow?.["Biodiversity.totalAreaHa"] != null
      ? Number(bioRow["Biodiversity.totalAreaHa"])
      : null;

  const hasData =
    (habitatCount != null && Number(habitatCount) > 0) ||
    (birdCount != null && Number(birdCount) > 0) ||
    hedgerows != null ||
    woodland != null;

  return (
    <InsightCard
      elevation={1}
      onClick={hasData ? onClick : undefined}
      sx={[
        !hasData ? { cursor: "default" } : {},
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        <CardHeader
          title="Biodiversity"
          description="Habitats, species and woodland"
        />
        <Box sx={{ pt: 1.5, borderTop: "1px solid #f4f4f5" }}>
          {isLoading ? (
            <CardLoading />
          ) : !hasData ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.4 }}
            >
              No biodiversity data available yet. Habitat and species
              information will appear here once your assessments have been
              processed.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <MetricRow
                label="Habitat Types"
                value={habitatCount != null ? String(habitatCount) : "-"}
                unit="recorded"
                color={COLORS.dahlia[500]}
              />
              <MetricRow
                label="Bird Species"
                value={birdCount != null ? String(birdCount) : "-"}
                unit="observed"
                color={COLORS.bellflower[500]}
              />
              <MetricRow
                label="Hedgerows"
                value={hedgerows != null ? hedgerows.toLocaleString() : "-"}
                unit="m"
                color={COLORS.daisy[500]}
              />
              <MetricRow
                label="Woodland"
                value={woodland != null ? woodland.toFixed(1) : "-"}
                unit="ha"
                color={COLORS.dahlia[300]}
              />
            </Box>
          )}
        </Box>
      </CardContent>
    </InsightCard>
  );
}

function StatItem({
  label,
  value,
}: {
  label: string;
  value: string | number | undefined;
}) {
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{ color: "#a1a1aa", lineHeight: 1.2, display: "block" }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, color: "#18181b", lineHeight: 1.3 }}
      >
        {value ?? "-"}
      </Typography>
    </Box>
  );
}

// --- Water & Soil Card ---

export function WaterSoilCard({ onClick, farmerGuid, sx }: InsightCardProps) {
  const { data: strengthsData, isLoading: strengthsLoading } =
    useNutrientStrengths(farmerGuid);
  const { data: weaknessesData, isLoading: weaknessesLoading } =
    useNutrientWeaknesses(farmerGuid);

  const isLoading = strengthsLoading || weaknessesLoading;
  const strengths = strengthsData?.data ?? [];
  const weaknesses = weaknessesData?.data ?? [];
  const total = strengths.length + weaknesses.length;
  const strengthPct = total > 0 ? (strengths.length / total) * 100 : 50;

  const hasData = strengths.length > 0 || weaknesses.length > 0;

  return (
    <InsightCard
      elevation={1}
      onClick={hasData ? onClick : undefined}
      sx={[
        !hasData ? { cursor: "default" } : {},
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        <CardHeader
          title="Water & Soil Quality"
          description="Nutrient management"
        />
        <Box sx={{ pt: 1.5, borderTop: "1px solid #f4f4f5" }}>
          {isLoading ? (
            <CardLoading />
          ) : !hasData ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.4 }}
            >
              No nutrient management data available yet. Water and soil quality
              insights will appear here once your assessments have been
              processed.
            </Typography>
          ) : (
            <>
              {total > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      height: 8,
                      borderRadius: 4,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${strengthPct}%`,
                        backgroundColor: COLORS.dahlia[500],
                        transition: "width 0.3s",
                      }}
                    />
                    <Box
                      sx={{
                        width: `${100 - strengthPct}%`,
                        backgroundColor: COLORS.daisy[500],
                        transition: "width 0.3s",
                      }}
                    />
                  </Box>
                </Box>
              )}
              <Box sx={{ display: "flex", gap: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: COLORS.dahlia[500],
                      flexShrink: 0,
                    }}
                  />
                  <Box>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 700, color: "#18181b", lineHeight: 1 }}
                    >
                      {strengths.length}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "#71717a", lineHeight: 1.2 }}
                    >
                      Strengths
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: COLORS.daisy[500],
                      flexShrink: 0,
                    }}
                  />
                  <Box>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 700, color: "#18181b", lineHeight: 1 }}
                    >
                      {weaknesses.length}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "#71717a", lineHeight: 1.2 }}
                    >
                      Weaknesses
                    </Typography>
                  </Box>
                </Box>
              </Box>
              {strengths.length > 0 && (
                <Box
                  sx={{
                    mt: 1.5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                  }}
                >
                  {strengths.slice(0, 2).map((s) => (
                    <Box
                      key={s["NutrientManagement.description"]}
                      sx={{
                        px: 1,
                        py: 0.5,
                        borderRadius: "8px",
                        backgroundColor: COLORS.dahlia[50],
                        border: `1px solid ${COLORS.dahlia[100]}`,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: COLORS.dahlia[700],
                          fontSize: "10px",
                          lineHeight: 1.3,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {s["NutrientManagement.description"]}
                      </Typography>
                    </Box>
                  ))}
                  {strengths.length > 2 && (
                    <Typography
                      variant="caption"
                      sx={{ color: "#a1a1aa", fontSize: "10px", pl: 0.5 }}
                    >
                      +{strengths.length - 2} more
                    </Typography>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      </CardContent>
    </InsightCard>
  );
}

// --- Grazing Management Card ---

function GrazingManagementCard({
  onClick,
  onNavigate,
}: {
  onClick: () => void;
  onNavigate: (path: string) => void;
}) {
  const { data: grazingData, isLoading } = useGrazingEventsByField();
  const { data: animalGroups } = useCurrentFarmAnimalGroups();
  const { data: fields } = useFields();

  const grazingRows = grazingData?.data ?? [];
  const topFields = grazingRows
    .map((r) => ({
      name: r["GrazingEvents.fieldName"],
      dm: Number(r["GrazingEvents.sumKgDmUsed"]) || 0,
    }))
    .filter((f) => f.dm > 0)
    .slice(0, 4);

  const totalFields = grazingRows.length;
  const totalDm = grazingRows.reduce(
    (sum, r) => sum + (Number(r["GrazingEvents.sumKgDmUsed"]) || 0),
    0
  );

  const hasData = topFields.length > 0 || totalDm > 0;

  // Find an animal group whose current field is not set
  const groupWithNoField = useMemo(() => {
    if (!animalGroups || !fields) return undefined;
    return animalGroups.find((ag) => {
      const assignedFields = fields.filter((f) => f.animalGroup === ag.guid);
      if (assignedFields.length === 0) return false;
      return !assignedFields.some((f) => f.animalGroupNow === ag.guid);
    });
  }, [animalGroups, fields]);

  return (
    <InsightCard
      elevation={1}
      onClick={hasData ? onClick : undefined}
      sx={
        !hasData
          ? {
              cursor: "default",
              display: "flex",
              flexDirection: "column",
              ...scrollCardSx,
            }
          : scrollCardSx
      }
    >
      <CardContent
        sx={{
          p: 2.5,
          "&:last-child": { pb: 2.5 },
          ...(!hasData && {
            display: "flex",
            flexDirection: "column",
            flex: 1,
          }),
        }}
      >
        <CardHeader
          title={hasData ? "Grazing Management" : "Track Your Grazing"}
          description={
            hasData
              ? `${new Date().getFullYear()} field usage`
              : "Field usage & dry matter"
          }
        />
        <Box
          sx={{
            pt: 1.5,
            borderTop: "1px solid #f4f4f5",
            ...(!hasData && {
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }),
          }}
        >
          {isLoading ? (
            <CardLoading />
          ) : !hasData ? (
            <>
              {groupWithNoField ? (
                <Box
                  sx={{
                    backgroundColor: "#FDFBEB",
                    border: "1px solid #F7E880",
                    borderRadius: 2,
                    px: 1.5,
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "#A48137", fontSize: "0.8rem" }}
                  >
                    {`"${groupWithNoField.name}" doesn't have a current field set.`}
                  </Typography>
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      analytics.track(AnalyticsEvents.INSIGHT_CTA_CLICKED, {
                        type: "set_current_field",
                      });
                      onNavigate(`/animal-group/${groupWithNoField.guid}`);
                    }}
                    sx={{
                      color: "#A48137",
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      padding: "4px 12px",
                      minWidth: "auto",
                      whiteSpace: "nowrap",
                      backgroundColor: "rgba(164, 129, 55, 0.1)",
                      "&:hover": {
                        backgroundColor: "rgba(164, 129, 55, 0.2)",
                      },
                    }}
                  >
                    Set Current Field
                  </Button>
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ lineHeight: 1.4 }}
                >
                  Record animal moves to see field usage and dry matter
                  consumption.
                </Typography>
              )}
            </>
          ) : (
            <>
              <Box sx={{ display: "flex", gap: 3, mb: 1.5 }}>
                <StatItem
                  label="Fields"
                  value={totalFields > 0 ? totalFields : undefined}
                />
                <StatItem
                  label="Total DM"
                  value={
                    totalDm > 0
                      ? `${totalDm.toLocaleString(undefined, {
                          maximumFractionDigits: 0,
                        })} kg`
                      : undefined
                  }
                />
              </Box>
              {topFields.length > 0 && (
                <Box sx={{ width: "100%", height: 80 }}>
                  <ResponsiveContainer>
                    <BarChart
                      data={topFields}
                      layout="vertical"
                      margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
                    >
                      <XAxis type="number" hide />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={70}
                        tick={{ fontSize: 10, fill: "#71717a" }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Bar
                        dataKey="dm"
                        fill={COLORS.dahlia[500]}
                        radius={[0, 4, 4, 0]}
                        maxBarSize={12}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </>
          )}
        </Box>
      </CardContent>
    </InsightCard>
  );
}

// --- Animal Performance Card ---

export function AnimalPerformanceCard({
  onClick,
  farmerGuid,
  sx,
}: InsightCardProps) {
  const { data: carcaseData, isLoading } = useCarcaseGauges(farmerGuid);
  const carcaseRow = carcaseData?.data?.[0];

  const dlwg =
    carcaseRow?.["CarcaseSummary.estLifetimeDlwgFarmer"] != null
      ? Number(carcaseRow["CarcaseSummary.estLifetimeDlwgFarmer"])
      : null;
  const slaughterAge =
    carcaseRow?.["CarcaseSummary.avgAgeAtSlaughterFarmer"] != null
      ? Number(carcaseRow["CarcaseSummary.avgAgeAtSlaughterFarmer"])
      : null;
  const carcaseWt =
    carcaseRow?.["CarcaseSummary.avgCarcaseColdWeightFarmer"] != null
      ? Number(carcaseRow["CarcaseSummary.avgCarcaseColdWeightFarmer"])
      : null;

  const hasData = dlwg != null || slaughterAge != null || carcaseWt != null;

  return (
    <InsightCard
      elevation={1}
      onClick={hasData ? onClick : undefined}
      sx={[
        !hasData ? { cursor: "default" } : {},
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        <CardHeader
          title="Animal Performance"
          description={
            hasData
              ? "Carcase metrics and grading"
              : "Liveweight, slaughter age & carcase"
          }
        />
        <Box sx={{ pt: 1.5, borderTop: "1px solid #f4f4f5" }}>
          {isLoading ? (
            <CardLoading />
          ) : !hasData ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.4 }}
            >
              Carcase data will appear here once slaughter records are
              available.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <MetricRow
                label="Daily Liveweight Gain"
                value={dlwg != null ? dlwg.toFixed(2) : "-"}
                unit="kg/day"
                color={COLORS.dahlia[500]}
              />
              <MetricRow
                label="Avg. Slaughter Age"
                value={
                  slaughterAge != null
                    ? Math.round(slaughterAge).toString()
                    : "-"
                }
                unit="months"
                color={COLORS.bellflower[500]}
              />
              <MetricRow
                label="Avg. Carcase Weight"
                value={
                  carcaseWt != null ? Math.round(carcaseWt).toString() : "-"
                }
                unit="kg"
                color={COLORS.daisy[500]}
              />
            </Box>
          )}
        </Box>
      </CardContent>
    </InsightCard>
  );
}

// --- Milk Quality Card ---

export function MilkQualityCard({ onClick, farmerGuid, sx }: InsightCardProps) {
  const { data: milkData, isLoading } = useMilkQualityFarmer(farmerGuid);
  const milkRow = milkData?.data?.[0];

  const fat =
    milkRow?.["MilkQuality.fatPercent"] != null
      ? Number(milkRow["MilkQuality.fatPercent"])
      : null;
  const protein =
    milkRow?.["MilkQuality.proteinPercent"] != null
      ? Number(milkRow["MilkQuality.proteinPercent"])
      : null;
  const scc =
    milkRow?.["MilkQuality.somaticCellCount"] != null
      ? Number(milkRow["MilkQuality.somaticCellCount"])
      : null;

  const hasData = fat != null || protein != null || scc != null;

  return (
    <InsightCard
      elevation={1}
      onClick={hasData ? onClick : undefined}
      sx={[
        !hasData ? { cursor: "default" } : {},
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        <CardHeader
          title="Milk Quality"
          description={
            hasData ? "Latest quality metrics" : "Quality metrics & volumes"
          }
        />
        <Box sx={{ pt: 1.5, borderTop: "1px solid #f4f4f5" }}>
          {isLoading ? (
            <CardLoading />
          ) : !hasData ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.4 }}
            >
              Milk quality data will appear here once reports are available.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <MetricRow
                label="Butterfat"
                value={fat != null ? fat.toFixed(2) : "-"}
                unit="%"
                color={COLORS.dahlia[500]}
              />
              <MetricRow
                label="Protein"
                value={protein != null ? protein.toFixed(2) : "-"}
                unit="%"
                color={COLORS.bellflower[500]}
              />
              <MetricRow
                label="Somatic Cell Count"
                value={scc != null ? Math.round(scc).toString() : "-"}
                unit="cells/ml"
                color={COLORS.daisy[500]}
              />
            </Box>
          )}
        </Box>
      </CardContent>
    </InsightCard>
  );
}

function MetricRow({
  label,
  value,
  unit,
  color,
}: {
  label: string;
  value: string;
  unit: string;
  color: string;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 0.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: color,
            flexShrink: 0,
          }}
        />
        <Typography variant="body2" sx={{ color: "#71717a" }}>
          {label}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 600, color: "#18181b" }}>
        {value}
        <Typography
          component="span"
          variant="caption"
          sx={{ color: "#a1a1aa", ml: 0.5 }}
        >
          {unit}
        </Typography>
      </Typography>
    </Box>
  );
}

// --- Exported card groups ---

const scrollCardSx = { minWidth: 280, maxWidth: 340 };

export function EnvironmentalCards({
  onOpenDetail,
}: {
  onOpenDetail: (category: InsightCategory) => void;
}) {
  const handleCardClick = (category: InsightCategory) => {
    analytics.track(AnalyticsEvents.INSIGHT_CARD_CLICKED, { category });
    onOpenDetail(category);
  };

  return (
    <HorizontalScrollContainer>
      <CarbonFootprintCard
        sx={scrollCardSx}
        onClick={() => handleCardClick("carbon-footprint")}
      />
      <BiodiversityCard
        sx={scrollCardSx}
        onClick={() => handleCardClick("biodiversity")}
      />
      <WaterSoilCard
        sx={scrollCardSx}
        onClick={() => handleCardClick("water-soil")}
      />
    </HorizontalScrollContainer>
  );
}

export function FarmPerformanceCards({
  onOpenDetail,
}: {
  onOpenDetail: (category: InsightCategory) => void;
}) {
  const navigate = useNavigate();
  const { programmeType } = useFarmerProgrammeType();
  const isDairy = programmeType === "DAIRY";

  const handleCardClick = (category: InsightCategory) => {
    analytics.track(AnalyticsEvents.INSIGHT_CARD_CLICKED, { category });
    onOpenDetail(category);
  };

  return (
    <>
      <GrazingManagementCard
        onClick={() => handleCardClick("grazing-management")}
        onNavigate={navigate}
      />
      {isDairy ? (
        <MilkQualityCard
          sx={scrollCardSx}
          onClick={() => handleCardClick("milk-quality")}
        />
      ) : (
        <AnimalPerformanceCard
          sx={scrollCardSx}
          onClick={() => handleCardClick("animal-performance")}
        />
      )}
    </>
  );
}
